import React, {useEffect, useState, useCallback} from 'react';
import "./ContactForm.css";

import {
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import Filters,
{
  TransportationModeOptions,
  StorageOptions,
  CommodityOptions
} from "../filters/Filters";
import Collapsible from '../../shared/collapsible/collapsible';
import Select from '../../shared/select/Select';
import submitContactForm from '../../../utilities/SubmitContactForm';

import {
  phoneNumberRegex,
  emailRegex
} from '../../../utilities/FormUtilities';

import config from '../../../config';

interface iProps {
  comment?: string;
  onSubmitCallback?: Function;
  stateOptions: {label: string; value: string}[];
  classIOptions: string[];
  filters?: React.RefObject<React.ElementRef<typeof Filters>>;
}

export default function ContactForm(props: iProps) {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [selectedStates, setSelectedStates] = useState(new Set<string>());
  const [selectedClassIRailroads, setSelectedClassIRailroads] = useState(new Set<string>());
  const [selectedTransportationModes, setSelectedTransportationModes] = useState(new Set<string>());
  const [selectedStorageOptions, setSelectedStorageOptions] = useState(new Set<string>());
  const [selectedCommodities, setSelectedCommodities] = useState(new Set<string>());
  const [additionalDetails, setAdditionalDetails] = useState('');

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [reCaptchaToken, setReCaptchaToken] = useState<string>();
  const [responseMessage, setResponseMessage] = useState<string>();

  const {executeRecaptcha} = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const token = await executeRecaptcha('ContactUs');
    setReCaptchaToken(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const initForm = () => {
    if (props.filters?.current) {
      const snapshot = props.filters?.current.getFiltersSnapshot();
      if (snapshot.state) {
        setSelectedStates(snapshot.state);
      }

      if (snapshot.classIRailroad) {
        setSelectedClassIRailroads(snapshot.classIRailroad);
      }

      if (snapshot.transportMode) {
        const options = TransportationModeOptions
          .filter(option => snapshot.transportMode.has(option.field))
          .map(option => option.label);
        setSelectedTransportationModes(new Set(options));
      }

      if (snapshot.storage) {
        const options = StorageOptions
          .filter(option => snapshot.storage.has(option.field))
          .map(option => option.label);
        setSelectedStorageOptions(new Set(options));
      }

      if (snapshot.commodity) {
        const options = CommodityOptions
          .filter(option => snapshot.commodity.has(option.field))
          .map(option => option.label);
        setSelectedCommodities(new Set(options));
      }
    }
  }
  useEffect(initForm, [props.filters]);

  const onSubmit = () => {
    const isValidPhoneNumber = phoneNumber.match(phoneNumberRegex);
    const isValidEmail = email.match(emailRegex);

    const errorMessages: string[] = [];

    if (!reCaptchaToken) {
      errorMessages.push("ReCaptcha failed.")
    }
    if (!name) {
      errorMessages.push("Name is required.")
    }
    if (!isValidPhoneNumber) {
      errorMessages.push("Valid Phone Number is required.");
    }
    if (!isValidEmail) {
      errorMessages.push("Valid Email is required.");
    }

    setValidationErrors(errorMessages);
    const validForm = errorMessages.length === 0;

    if (!validForm || !reCaptchaToken) return;

    const subject = 'GeoConnect - Transloading'
    const body = `Name: ${name}\n`
      + `Phone Number: ${phoneNumber}\n`
      + `Email: ${email}\n`
      + `Company Name: ${companyName}\n`
      + 'Request Details\n'
      + `States: ${Array.from(selectedStates).join(", ")}\n`
      + `Class I Railroads: ${Array.from(selectedClassIRailroads).join(", ")}\n`
      + `Transportation Modes: ${Array.from(selectedTransportationModes).join(", ")}\n`
      + `Storage: ${Array.from(selectedStorageOptions).join(", ")}\n`
      + `Commodities: ${Array.from(selectedCommodities).join(", ")}\n`
      + `Additional Details: ${additionalDetails}\n`;

    submitContactForm(reCaptchaToken, config.transloading.contactFormId, subject, email, body).then(response => {
      response.json().then(json => {
        setResponseMessage(json.message);

        if (json.status === 'mail_sent' && props.onSubmitCallback) {
          props.onSubmitCallback();
        }
      });
    }).catch((err) => {
      console.log(err);
      setResponseMessage("A network error occured. Please try again later.")
    });
  }

  const onStateCheckboxChange = (evt: any) => {
    const options = new Set(selectedStates);

    if (evt.target.checked) {
      options.add(evt.target.value);
    } else {
      options.delete(evt.target.value);
    }
    setSelectedStates(options);
  };

  const onClassICheckboxChange = (event: any) => {
    const options = new Set(selectedClassIRailroads);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedClassIRailroads(options);
  }

  const onTransportModeCheckboxChange = (event: any) => {
    const options = new Set(selectedTransportationModes);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedTransportationModes(options);
  }

  const onStorageCheckboxChange = (event: any) => {
    const options = new Set(selectedStorageOptions);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedStorageOptions(options);
  }

  const onCommodityCheckboxChange = (event: any) => {
    const options = new Set(selectedCommodities);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedCommodities(options);
  }

  return (
    <div className="transloading-contact-form">
      <div className='flex-form'>
        <div className="input-container">
          <input type='text'
            value={name}
            onChange={evt => {setName(evt.target.value)}}
            name='your-name'
            placeholder='*Name' />
        </div>
        <div className="input-container">
          <input type='text'
            value={phoneNumber}
            onChange={evt => {setPhoneNumber(evt.target.value)}}
            name='phone'
            placeholder='*Phone Number' />
        </div>
        <div className="input-container">
          <input type='text'
            value={email}
            onChange={evt => {setEmail(evt.target.value)}}
            name='e-mail'
            placeholder='*Email' />
        </div>
        <div className="input-container">
          <input type='text'
            value={companyName}
            onChange={evt => {setCompanyName(evt.target.value)}}
            name='Company-Name'
            placeholder='Company Name' />
        </div>
      </div>
      <Collapsible buttonLabel='Request Details'>
        <div className='details-section'>
          <div className="flex-form">
            <div className='input-container'>
              <Select label={'States'} labelPosition={'inline'} multiSelect={true}>
                <div className='state-select'>
                  {props.stateOptions?.map((option: any) =>
                    <div key={option.label} className="state-option">
                      <input
                        type="checkbox"
                        id={`contact-class-i-checkbox-${option.value}`}
                        value={option.value}
                        checked={selectedStates.has(option.value)}
                        onChange={onStateCheckboxChange} />
                      <label htmlFor={`contact-class-i-checkbox-${option.value}`}>{option.label}</label>
                    </div>
                  )}
                </div>
              </Select>
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Class I Railroads</div>
              {props.classIOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`contact-class-i-checkbox-${index}`}
                    onChange={onClassICheckboxChange}
                    checked={selectedClassIRailroads.has(option)}
                    value={option}
                  />
                  <label htmlFor={`contact-class-i-checkbox-${index}`}>{option}</label>
                </div>
              )}
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Transportation Modes</div>
              {TransportationModeOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`contact-transport-mode-checkbox-${index}`}
                    onChange={onTransportModeCheckboxChange}
                    checked={selectedTransportationModes.has(option.label)}
                    value={option.label}
                  />
                  <label htmlFor={`contact-transport-mode-checkbox-${index}`}>{option.label} Served</label>
                </div>
              )}
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Storage</div>
              {StorageOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`contact-storage-checkbox-${index}`}
                    onChange={onStorageCheckboxChange}
                    checked={selectedStorageOptions.has(option.label)}
                    value={option.label}
                  />
                  <label htmlFor={`contact-storage-checkbox-${index}`}>{option.label}</label>
                </div>
              )}
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Commodities</div>
              {CommodityOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`contact-commodity-checkbox-${index}`}
                    onChange={onCommodityCheckboxChange}
                    checked={selectedCommodities.has(option.label)}
                    value={option.label}
                  />
                  <label htmlFor={`contact-commodity-checkbox-${index}`}>{option.label}</label>
                </div>
              )}
            </div>

            <div className="input-container">
              <textarea value={additionalDetails}
                onChange={evt => {setAdditionalDetails(evt.target.value)}}
                name='text-area'
                placeholder='Additional Details' />
            </div>
          </div>
        </div>
      </Collapsible>
      <div className='small-font'>
        * Required Fields
      </div>
      <div className='small-font'>
        This site is protected by reCAPTCHA and the Google&nbsp;
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </div>
      {validationErrors.map((msg, index) =>
        <div key={index} className='small-font color-orange'>
          {msg}
        </div>
      )}
      <div className="input-container">
        <button onClick={onSubmit}>Submit</button>
      </div>
      {responseMessage &&
        <div>{responseMessage}</div>
      }
    </div>
  )
}